// Master-data import screen (WP 0.4/0.5): upload, history, held changes, dead letters,
// work items, and the recent apply log. Refreshes on `import`, `hold` and `replica` events.

import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { api, type DeadLetter, type Hold, type ImportRecord, type Json } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, JsonBlock, Loading, Pill, StatusPill, shortId } from '../components/ui.tsx';
import { useAction, useLoad, type Load } from '../hooks.ts';
import { href, navigate } from '../router.ts';
import { useSession } from '../session.tsx';
import { useRefreshOn } from '../stream.tsx';
import { fmtBytes, fmtLocal, relAge } from '../time.ts';

/* ---------- upload ---------- */

function Upload() {
  const { isAdmin, has } = useSession();
  const canReadCutover = has('supervisor', 'admin');
  // Phase 6 (contract §6.11): once the live ERP feed is promoted, imports are off. Errors fall back to the drop zone;
  // the server refuses the upload anyway.
  const cutover = useLoad((signal) => (canReadCutover ? api.cutover(signal).catch(() => null) : Promise.resolve(null)), [canReadCutover]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [drag, setDrag] = useState(false);
  const [file, setFile] = useState<string | null>(null);
  const up = useAction(async (f: File) => {
    const text = await f.text();
    try {
      JSON.parse(text);
    } catch (e) {
      throw new Error(`${f.name} is not valid JSON: ${e instanceof Error ? e.message : String(e)}`);
    }
    const rec = await api.uploadImport(f.name, text);
    navigate(href.importRecord(rec.id));
    return rec;
  });

  if (cutover.data && !cutover.data.importsAllowed) {
    return (
      <Card title={<><Icon name="upload" />Upload an import file</>}>
        <div className="note">
          <Icon name="lock" /> Imports are off: the live ERP feed is the source of master data (contract §6.11). Change data in the ERP.{' '}
          <a href={href.erpSync()}>ERP Sync</a>
        </div>
      </Card>
    );
  }

  if (!isAdmin) {
    return (
      <Card title={<><Icon name="upload" />Upload an import file</>}>
        <div className="note">
          <Icon name="lock" /> Uploading and confirming imports needs the admin role. You can still review history, previews, and held changes.
        </div>
      </Card>
    );
  }

  const pick = (f: File | undefined) => {
    if (!f || up.busy) return;
    setFile(f.name);
    void up.run(f);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDrag(false);
    pick(e.dataTransfer.files[0]);
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  return (
    <Card title={<><Icon name="upload" />Upload an import file</>}>
      <div
        className={`drop${drag ? ' over' : ''}${up.busy ? ' busy' : ''}`}
        role="button"
        tabIndex={0}
        aria-label="Choose an import file, or drop one here"
        aria-busy={up.busy}
        onClick={() => inputRef.current?.click()}
        onKeyDown={onKey}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
      >
        <Icon name="file" />
        {up.busy ? (
          <span>Validating {file}…</span>
        ) : (
          <span>
            Drop an import file (<span className="mono">.json</span>, contract §6.10) here, or <u>choose a file</u>.
          </span>
        )}
        <span className="note">The file is validated and previewed. Nothing changes until you confirm.</span>
        <input
          ref={inputRef}
          type="file"
          accept=".json,application/json"
          hidden
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
      <InlineError error={up.error} />
    </Card>
  );
}

/* ---------- history ---------- */

function SummaryCells({ r }: { r: ImportRecord }) {
  const s = r.summary;
  if (!s) return <span className="dim">{r.errors.length ? `${r.errors.length} error${r.errors.length === 1 ? '' : 's'}` : '—'}</span>;
  return (
    <span className="sumline mono">
      <span title="creates">+{s.creates}</span>
      <span title="updates">~{s.updates}</span>
      <span title="deletes">−{s.deletes}</span>
      {s.reactivates > 0 && <span title="reactivates">↺{s.reactivates}</span>}
      {s.holds > 0 && <span className="warn-t" title="holds">⏸{s.holds}</span>}
      {s.warnings > 0 && <span className="warn-t" title="warnings">!{s.warnings}</span>}
    </span>
  );
}

function scopeText(scope: Json): string {
  if (scope === null || scope === undefined) return '—';
  if (typeof scope === 'string') return scope;
  try {
    const t = JSON.stringify(scope);
    return t.length > 60 ? `${t.slice(0, 57)}…` : t;
  } catch {
    return '—';
  }
}

function History({ load }: { load: Load<ImportRecord[]> }) {
  const rows = load.data;
  return (
    <Card
      title={<><Icon name="list" />Import history</>}
      right={
        <button type="button" className="btn sm" onClick={load.reload} disabled={load.loading}>
          <Icon name="sync" />
          Refresh
        </button>
      }
    >
      {load.error && !rows ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load import history" />
      ) : !rows ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>No imports yet.</Empty>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Uploaded</th>
                <th scope="col">File</th>
                <th scope="col">By</th>
                <th scope="col">Status</th>
                <th scope="col">Scope</th>
                <th scope="col">Plan</th>
                <th scope="col" className="num">Size</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="click" onClick={() => navigate(href.importRecord(r.id))}>
                  <td className="mono nowrap" title={r.uploadedAt}>
                    {fmtLocal(r.uploadedAt)}
                  </td>
                  <td>
                    <a href={href.importRecord(r.id)} onClick={(e) => e.stopPropagation()} className="strong">
                      {r.fileName}
                    </a>
                    <div className="note mono" title={r.sha256}>
                      sha256 {shortId(r.sha256, 12)}
                    </div>
                  </td>
                  <td className="ell">{r.uploadedBy}</td>
                  <td>
                    <StatusPill status={r.status} />
                  </td>
                  <td className="mono small">
                    {r.source} · {scopeText(r.scope)}
                  </td>
                  <td>
                    <SummaryCells r={r} />
                  </td>
                  <td className="mono num">{fmtBytes(r.size)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ---------- held changes ---------- */

function HoldRow({ h, canDecide, onDone }: { h: Hold; canDecide: boolean; onDone: () => void }) {
  const [mode, setMode] = useState<'confirm' | 'reject' | null>(null);
  const [note, setNote] = useState('');
  const act = useAction(async (m: 'confirm' | 'reject') => {
    const n = note.trim() || undefined;
    const r = m === 'confirm' ? await api.confirmHold(h.id, n) : await api.rejectHold(h.id, n);
    setMode(null);
    setNote('');
    onDone();
    return r;
  });
  return (
    <>
      <tr>
        <td className="mono nowrap">{fmtLocal(h.createdAt)}</td>
        <td>
          <div>
            {h.entityType} <span className="mono dim">{h.entityId}</span>
          </div>
          {h.siteId && (
            <a href={href.site(h.siteId)} className="note">
              {h.siteName ?? h.siteId}
            </a>
          )}
        </td>
        <td>
          <Pill tone="warn">{h.change.replace(/_/g, ' ')}</Pill>
        </td>
        <td>
          <div className="mono small">{h.rule}</div>
          <div className="note">{h.detail}</div>
        </td>
        <td className="mono num" title="Later changes waiting behind this hold">
          {h.queuedCount}
        </td>
        <td>
          <StatusPill status={h.status} />
          {h.decidedBy && <div className="note">{h.decidedBy} · {fmtLocal(h.decidedAt)}</div>}
        </td>
        <td className="nowrap">
          {canDecide && h.status === 'held' && !mode && (
            <span className="btns">
              <button type="button" className="btn sm" onClick={() => setMode('confirm')}>
                <Icon name="check" />
                Confirm
              </button>
              <button type="button" className="btn sm" onClick={() => setMode('reject')}>
                <Icon name="x" />
                Reject
              </button>
            </span>
          )}
        </td>
      </tr>
      {mode && (
        <tr className="subrow">
          <td colSpan={7}>
            <div className={`confirmbar ${mode === 'reject' ? 'danger' : ''}`}>
              <span>
                {mode === 'confirm' ? 'Apply this held change' : 'Reject this held change; the replica keeps its current value'}
                {h.queuedCount > 0 ? ` (${h.queuedCount} queued change${h.queuedCount === 1 ? '' : 's'} follow it)` : ''}.
              </span>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" aria-label="Decision note" autoFocus />
              <button type="button" className={`btn ${mode === 'confirm' ? 'pri' : 'danger'}`} disabled={act.busy} onClick={() => void act.run(mode)}>
                {act.busy ? 'Working…' : mode === 'confirm' ? 'Yes, apply' : 'Yes, reject'}
              </button>
              <button type="button" className="btn" disabled={act.busy} onClick={() => setMode(null)}>
                Back
              </button>
              <InlineError error={act.error} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Holds() {
  const s = useSession();
  const canDecide = s.has('supervisor', 'admin');
  const [status, setStatus] = useState<'held' | ''>('held');
  const load = useLoad((signal) => api.holds(status || undefined, signal), [status]);
  useRefreshOn(['hold', 'import'], load.reload);
  const rows = load.data;
  return (
    <Card
      title={<><Icon name="pause" />Held changes{rows && status === 'held' ? <span className="n">{rows.length}</span> : null}</>}
      right={
        <select value={status} onChange={(e) => setStatus(e.target.value === 'held' ? 'held' : '')} aria-label="Hold status">
          <option value="held">Waiting</option>
          <option value="">All</option>
        </select>
      }
    >
      <div className="note">Safety holds stop risky changes (deletes, deactivations, status changes) until a supervisor decides.{canDecide ? '' : ' Deciding needs supervisor or admin.'}</div>
      {load.error && !rows ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load held changes" />
      ) : !rows ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>{status === 'held' ? 'Nothing is waiting for a decision.' : 'No holds yet.'}</Empty>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Held</th>
                <th scope="col">Entity</th>
                <th scope="col">Change</th>
                <th scope="col">Rule</th>
                <th scope="col" className="num">Queued</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <HoldRow key={h.id} h={h} canDecide={canDecide} onDone={load.reload} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ---------- dead letters ---------- */

function DeadLetterRow({ d, canAct, onDone }: { d: DeadLetter; canAct: boolean; onDone: () => void }) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [showMsg, setShowMsg] = useState(false);
  const [result, setResult] = useState<Json | undefined>(undefined);
  const retry = useAction(async () => {
    const r = await api.retryDeadLetter(d.id);
    setResult(r.result);
    onDone();
  });
  const discard = useAction(async () => {
    await api.discardDeadLetter(d.id);
    setConfirmDiscard(false);
    onDone();
  });
  const open = d.status !== 'discarded' && d.status !== 'resolved';
  return (
    <>
      <tr>
        <td className="mono nowrap">{fmtLocal(d.createdAt)}</td>
        <td>
          {d.entityType} <span className="mono dim">{d.entityId}</span>
        </td>
        <td>
          <div className="mono small">{d.reason}</div>
          {d.detail && <div className="note">{d.detail}</div>}
        </td>
        <td>
          <StatusPill status={d.status} />
        </td>
        <td className="nowrap">
          <span className="btns">
            <button type="button" className="btn sm" onClick={() => setShowMsg((v) => !v)} aria-expanded={showMsg}>
              Message
            </button>
            {canAct && open && (
              <>
                <button type="button" className="btn sm" disabled={retry.busy} onClick={() => void retry.run()}>
                  <Icon name="sync" />
                  {retry.busy ? 'Retrying…' : 'Retry'}
                </button>
                <button type="button" className="btn sm" onClick={() => setConfirmDiscard(true)} disabled={confirmDiscard}>
                  Discard
                </button>
              </>
            )}
          </span>
          <InlineError error={retry.error} />
        </td>
      </tr>
      {(confirmDiscard || showMsg || result !== undefined) && (
        <tr className="subrow">
          <td colSpan={5}>
            {confirmDiscard && (
              <div className="confirmbar danger">
                <span>Discard this dead letter? The change will not be applied.</span>
                <button type="button" className="btn danger" disabled={discard.busy} onClick={() => void discard.run()}>
                  {discard.busy ? 'Discarding…' : 'Yes, discard'}
                </button>
                <button type="button" className="btn" onClick={() => setConfirmDiscard(false)}>
                  Back
                </button>
                <InlineError error={discard.error} />
              </div>
            )}
            {result !== undefined && (
              <div className="sec">
                <span className="lbl">Retry result</span>
                <JsonBlock value={result} maxHeight={200} />
              </div>
            )}
            {showMsg && <JsonBlock value={d.message} maxHeight={300} />}
          </td>
        </tr>
      )}
    </>
  );
}

function DeadLetters() {
  const s = useSession();
  const load = useLoad((signal) => api.deadLetters(signal), []);
  useRefreshOn(['import', 'replica'], load.reload, 1000);
  const rows = load.data;
  return (
    <Card title={<><Icon name="alert" />Dead letters{rows ? <span className="n">{rows.length}</span> : null}</>}>
      <div className="note">Changes the applier could not apply (unknown parent, version conflict, bad reference).{s.isAdmin ? '' : ' Retry and discard need admin.'}</div>
      {load.error && !rows ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load dead letters" />
      ) : !rows ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>No dead letters.</Empty>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Created</th>
                <th scope="col">Entity</th>
                <th scope="col">Reason</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <DeadLetterRow key={d.id} d={d} canAct={s.isAdmin} onDone={load.reload} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ---------- work items ---------- */

function WorkItems() {
  const load = useLoad((signal) => api.workItems('open', signal), []);
  useRefreshOn(['hold', 'import', 'replica'], load.reload, 1000);
  const rows = load.data;
  return (
    <Card title={<><Icon name="list" />Open work items{rows ? <span className="n">{rows.length}</span> : null}</>}>
      {load.error && !rows ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load work items" />
      ) : !rows ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>No open work items.</Empty>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Created</th>
                <th scope="col">Kind</th>
                <th scope="col">Item</th>
                <th scope="col">Site</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.id}>
                  <td className="mono nowrap">{fmtLocal(w.createdAt)}</td>
                  <td>
                    <span className="typ">{w.kind.replace(/_/g, ' ')}</span>
                  </td>
                  <td>
                    <div className="strong">{w.title}</div>
                    {w.detail && <div className="note">{w.detail}</div>}
                  </td>
                  <td>{w.siteId ? <a href={href.site(w.siteId)} className="mono">{w.siteId}</a> : <span className="dim">—</span>}</td>
                  <td>
                    <StatusPill status={w.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ---------- apply log ---------- */

const RESULT_CLS: Record<string, string> = { applied: 'ok', ok: 'ok', skipped: 'skip', stale: 'skip', unchanged: 'skip', held: 'wait', dead_letter: 'bad', failed: 'bad', rejected: 'bad' };

function ApplyLog() {
  const load = useLoad((signal) => api.applyLog(200, signal), []);
  useRefreshOn(['replica', 'import', 'hold'], load.reload, 1200);
  const [all, setAll] = useState(false);
  const rows = load.data;
  const shown = rows ? (all ? rows : rows.slice(0, 50)) : [];
  return (
    <Card title={<><Icon name="log" />Recent apply log</>}>
      {load.error && !rows ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the apply log" />
      ) : !rows ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>Nothing has been applied yet.</Empty>
      ) : (
        <>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th scope="col">At</th>
                  <th scope="col">Source</th>
                  <th scope="col">Entity</th>
                  <th scope="col">Site</th>
                  <th scope="col" className="num">Version</th>
                  <th scope="col">Result</th>
                  <th scope="col">Effect / reason</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((e) => (
                  <tr key={e.id}>
                    <td className="mono nowrap" title={relAge(e.at)}>
                      {fmtLocal(e.at)}
                    </td>
                    <td className="small">
                      {e.source}
                      {e.importFileId && (
                        <div>
                          <span className="note mono">{shortId(e.importFileId)}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      {e.entityType} <span className="mono dim">{e.entityId}</span>
                    </td>
                    <td>{e.siteId ? <a href={href.site(e.siteId)} className="mono">{e.siteId}</a> : <span className="dim">—</span>}</td>
                    <td className="mono num">{e.version ?? '—'}</td>
                    <td>
                      <span className={`res ${RESULT_CLS[e.result] ?? 'skip'}`}>{e.result.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="small">
                      {e.effect && <span className="mono">{e.effect}</span>}
                      {e.reason && <div className="note">{e.reason}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 50 && (
            <div className="tfoot">
              <span className="note">
                Showing {shown.length} of {rows.length}
              </span>
              <span className="sp" />
              <button type="button" className="btn sm" onClick={() => setAll((v) => !v)}>
                {all ? 'Show fewer' : 'Show all'}
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/* ---------- screen ---------- */

export function ImportsScreen() {
  const history = useLoad((signal) => api.imports(signal), []);
  useRefreshOn(['import'], history.reload);
  const pending = (history.data ?? []).filter((r) => r.status === 'previewed');
  return (
    <div className="page">
      <div className="page-h">
        <div>
          <h2>Master-data imports</h2>
          <p>Load companies, sites, cameras, devices, contacts, and site maps from an ERP export. Until Phase 6 this is how master data arrives.</p>
        </div>
      </div>
      {pending.length > 0 && (
        <div className="banner warnish">
          <Icon name="alert" />
          <span>
            {pending.length} previewed import{pending.length === 1 ? ' is' : 's are'} waiting for confirmation:{' '}
            {pending.slice(0, 3).map((r, i) => (
              <span key={r.id}>
                {i > 0 && ', '}
                <a href={href.importRecord(r.id)}>{r.fileName}</a>
              </span>
            ))}
            {pending.length > 3 ? '…' : ''}
          </span>
        </div>
      )}
      <div className="cols">
        <Upload />
        <Holds />
      </div>
      <History load={history} />
      <div className="cols">
        <DeadLetters />
        <WorkItems />
      </div>
      <ApplyLog />
    </div>
  );
}
